import { BRAND, logoWidth } from "./lib/config";
import { useCallback, useEffect, useRef, useState, type ComponentType } from "react";
import { Icon, type IconName } from "./components/Icon";
import { signOut, useSession, type SignedInUser } from "./lib/auth";
import { getProfile, listCrew, newLeads } from "./lib/data";
import { hashParams, useHash } from "./lib/route";
import { COUNTS_EVENT } from "./lib/events";
import { humanise } from "./lib/format";
import type { Profile } from "./lib/supabase";
import { missingTickets, rosteredCrew } from "./lib/tickets";
import { myCrewRecord } from "./modules/field/field-data";
import { Incidents } from "./modules/field/Incidents";
import { MyShiftsLink, MyShiftsScreen } from "./modules/field/MyShiftsScreen";
import { FIELD_NAV } from "./modules/field/nav";
import { Clients } from "./modules/clients/Clients";
import { CLIENTS_NAV } from "./modules/clients/nav";
import { Bookings } from "./screens/Bookings";
import { Crew } from "./screens/Crew";
import { Crm } from "./screens/Crm";
import { CrewApp } from "./screens/CrewApp";
import { PackView } from "./screens/CrewPack";
import { Integrations } from "./screens/Integrations";
import { Jobs } from "./screens/Jobs";
import { Terms } from "./screens/Terms";
import { Leads } from "./screens/Leads";
import { Login } from "./screens/Login";
import { Quotes } from "./screens/Quotes";
import { RateCard } from "./screens/RateCard";
import { Today } from "./screens/Today";

type CountKey = "newLeads" | "missingTickets";
interface RouteDef { label: string; icon: IconName; Screen: ComponentType; count?: CountKey; hint?: string; hidden?: boolean }

const ROUTES = {
  "#/today": { label: "Today", icon: "today", Screen: Today },
  // vocab-ok: field module route, shown only to staff linked to a crew record
  "#/my": { label: FIELD_NAV.myShifts.label, icon: "hardhat", Screen: MyShiftsScreen, hint: FIELD_NAV.myShifts.hint },
  "#/leads": { label: "Leads", icon: "inbox", Screen: Leads, count: "newLeads" },
  "#/clients": { label: CLIENTS_NAV.label, icon: "mail", Screen: Clients, hint: CLIENTS_NAV.hint },
  "#/quotes": { label: "Quotes", icon: "document", Screen: Quotes },
  "#/jobs": { label: "Jobs", icon: "file", Screen: Jobs, hint: "Shifts, crew, documents" },
  "#/bookings": { label: "Bookings", icon: "calendar", Screen: Bookings },
  "#/crew": { label: "Crew & tickets", icon: "hardhat", Screen: Crew, count: "missingTickets" },
  "#/incidents": { label: FIELD_NAV.incidents.label, icon: "warning", Screen: Incidents, hint: FIELD_NAV.incidents.hint },
  "#/crm": { label: "CRM", icon: "sync", Screen: Crm, hint: "Sync status" },
  "#/rates": { label: "Rate card", icon: "dollar", Screen: RateCard },
  "#/terms": { label: BRAND.termsTitle, icon: "check", Screen: Terms, hint: "Signed agreements" },
  "#/integrations": { label: "Xero", icon: "external", Screen: Integrations, hint: "Accounting" },
  "#/pack": { label: "Crew view", icon: "hardhat", Screen: PackPreview, hidden: true },
} satisfies Record<string, RouteDef>;
type Route = keyof typeof ROUTES;
// The Xero screen is only listed when the client layer switches Xero on (features.xero); field screens need a
// field block, and "#/my" a login linked to a crew record.
const routeKeys = (linked: boolean) =>
  (Object.keys(ROUTES) as Route[]).filter(
    (r) => !(ROUTES[r] as RouteDef).hidden && (r !== "#/integrations" || BRAND.xero) && (r !== "#/terms" || BRAND.agreements) && (r !== "#/incidents" || BRAND.field) && (r !== "#/clients" || BRAND.onboarding) && (r !== "#/my" || linked),
  );
const isRoute = (h: string): h is Route => h in ROUTES;
const COUNT_LABEL: Record<CountKey, (n: number) => string> = {
  newLeads: (n) => `${n} new`,
  missingTickets: FIELD_NAV.missingTicketsBadge,
};

/** The screen for the current hash: "#/jobs/123" is the Jobs screen. */
function useHashRoute(): Route {
  const hash = useHash();
  const base = `#/${hash.replace(/^#\/?/, "").split("/")[0] ?? ""}`;
  return isRoute(base) ? base : "#/today";
}

/** Office preview of a shift's job pack (#/pack/<booking id>), as crew see it. */
function PackPreview() {
  const [id] = hashParams(useHash());
  return (
    <section className="page" aria-labelledby="page-title" style={{ maxWidth: 760 }}>
      {id ? <PackView key={id} bookingId={id} backHref="#/jobs" backLabel="Jobs" /> : <p>No shift chosen.</p>}
    </section>
  );
}

function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

function useNavCounts(route: Route): Partial<Record<CountKey, number>> {
  const [counts, setCounts] = useState<Partial<Record<CountKey, number>>>({});
  const load = useCallback(() => {
    // Counts are a convenience: if a query fails the badge is simply left out.
    newLeads(1).then((r) => setCounts((c) => ({ ...c, newLeads: r.count })), () => undefined);
    Promise.all([listCrew(), rosteredCrew()]).then(([crew, rostered]) => setCounts((c) => ({ ...c, missingTickets: missingTickets(crew, rostered).length })), () => undefined);
  }, []);
  useEffect(load, [load, route]);
  useEffect(() => {
    window.addEventListener(COUNTS_EVENT, load);
    return () => window.removeEventListener(COUNTS_EVENT, load);
  }, [load]);
  return counts;
}

const RAIL_KEY = "ops-portal:rail";
function readRailPref(): boolean | null {
  try {
    const v = window.localStorage.getItem(RAIL_KEY);
    return v === null ? null : v === "1";
  } catch {
    return null;
  }
}

export function App() {
  const user = useSession();
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined);
  useEffect(() => {
    if (!user) return setProfile(undefined);
    let live = true;
    getProfile(user.id).then((p) => live && setProfile(p), () => live && setProfile(null));
    return () => { live = false; };
  }, [user?.id]);
  if (user === undefined) return null;
  if (!user) return <Login />;
  if (profile === undefined) return null;
  // Crew (controllers) get the phone-first job pack app; staff get the full portal.
  if (profile?.role === "controller") return <CrewApp user={user} profile={profile} />;
  return <Shell user={user} initialProfile={profile} />;
}

function Shell({ user, initialProfile }: { user: SignedInUser; initialProfile: Profile | null }) {
  const route = useHashRoute();
  const isMobile = useMedia("(max-width: 719px)");
  const isTablet = useMedia("(max-width: 1099px)");
  const [railPref, setRailPref] = useState<boolean | null>(readRailPref);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const profile = initialProfile;
  const counts = useNavCounts(route);
  // Staff who also work on site (login linked to a crew record) get "My shifts".
  const [linked, setLinked] = useState(false);
  useEffect(() => {
    let live = true;
    myCrewRecord(user.id).then((m) => live && setLinked(Boolean(m)), () => undefined);
    return () => { live = false; };
  }, [user.id]);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const lastRoute = useRef(route);

  const rail = !isMobile && (railPref ?? isTablet);

  // Route change: close the drawer and move focus to the new page title.
  useEffect(() => {
    setDrawerOpen(false);
    if (lastRoute.current === route) return;
    lastRoute.current = route;
    window.scrollTo(0, 0);
    requestAnimationFrame(() => document.getElementById("page-title")?.focus({ preventScroll: true }));
  }, [route]);

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!drawerOpen) return;
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        menuButton.current?.focus();
      } else if (e.key === "Tab") {
        // Keep keyboard focus inside the open drawer.
        const items = document.getElementById("sidebar")?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])");
        if (!items?.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [drawerOpen]);

  function toggleRail() {
    const next = !rail;
    setRailPref(next);
    try {
      window.localStorage.setItem(RAIL_KEY, next ? "1" : "0");
    } catch {
      /* per-viewer convenience only */
    }
  }

  const { Screen } = ROUTES[route];
  const name = profile?.full_name || user.email;
  const shellClass = ["shell", rail && "shell--rail", isMobile && "shell--mobile", drawerOpen && "shell--drawer-open"].filter(Boolean).join(" ");

  return (
    <div className={shellClass}>
      <button type="button" className="skip-link" onClick={() => document.getElementById("main")?.focus()}>
        Skip to content
      </button>

      {isMobile && (
        <div className="mobilebar">
          <button
            ref={menuButton}
            type="button"
            className="icon-btn icon-btn--dark"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="sidebar"
            onClick={() => setDrawerOpen(true)}
          >
            <Icon name="menu" size={24} />
            {(counts.newLeads ?? 0) + (counts.missingTickets ?? 0) > 0 && <span className="icon-btn__dot" aria-hidden="true" />}
          </button>
          <a href="#/today" className="mobilebar__logo">
            <img src={BRAND.logoSmall} alt={`${BRAND.name}, today`} width={logoWidth(41)} height="41" />
          </a>
          <span className="mobilebar__title">{ROUTES[route].label}</span>
        </div>
      )}

      <aside
        id="sidebar"
        className="side"
        aria-label="Portal navigation"
        inert={isMobile && !drawerOpen}
        {...(isMobile ? { role: "dialog", "aria-modal": drawerOpen } : {})}
      >
        <div className="side__top">
          <a href="#/today" className="side__logo" title={rail ? BRAND.name : undefined}>
            <img
              src={BRAND.logoSmall}
              srcSet={`${BRAND.logoSmall} 320w, ${BRAND.logo} 640w`}
              sizes={rail ? "52px" : "132px"}
              alt={`${BRAND.name} staff portal`}
              width={logoWidth(251)}
              height="251"
            />
          </a>
          {isMobile && (
            <button ref={closeButton} type="button" className="icon-btn icon-btn--dark" aria-label="Close menu" onClick={() => { setDrawerOpen(false); menuButton.current?.focus(); }}>
              <Icon name="close" size={24} />
            </button>
          )}
        </div>

        <nav className="side__nav" aria-label="Main">
          <ul>
            {routeKeys(linked).map((r) => {
              const def: RouteDef = ROUTES[r];
              const n = def.count ? counts[def.count] : undefined;
              return (
                <li key={r}>
                  <a href={r} className="navlink" aria-current={r === route ? "page" : undefined} title={rail ? def.label : undefined}>
                    <Icon name={def.icon} size={20} className="navlink__icon" />
                    <span className="navlink__label">
                      {def.label}
                      {def.hint && <small className="navlink__hint">{def.hint}</small>}
                    </span>
                    {def.count && n !== undefined && n > 0 && (
                      <span className={`badge badge--${def.count === "missingTickets" ? "primary" : "accent"}`}>
                        <span aria-hidden="true">{n}</span>
                        <span className="sr-only">, {COUNT_LABEL[def.count](n)}</span>
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="side__foot">
          <div className="who" title={rail ? `${name}, ${user.email}` : undefined}>
            <span className="who__avatar" aria-hidden="true">{initials(name)}</span>
            <span className="who__text">
              <span className="who__label">Signed in as</span>
              <span className="who__name">{name}</span>
              {profile?.full_name && <span className="who__email">{user.email}</span>}
              {profile?.role && <span className="who__role">{humanise(profile.role)}</span>}
            </span>
          </div>
          <button type="button" className="side__btn" onClick={() => void signOut()} title={rail ? "Sign out" : undefined}>
            <Icon name="logout" size={20} />
            <span className="side__btn-label">Sign out</span>
          </button>
          {!isMobile && (
            <button type="button" className="side__btn side__btn--quiet" onClick={toggleRail} aria-pressed={rail} title={rail ? "Expand menu" : undefined}>
              <Icon name={rail ? "expand" : "collapse"} size={20} />
              <span className="side__btn-label">Collapse menu</span>
            </button>
          )}
        </div>
      </aside>

      {isMobile && drawerOpen && <div className="scrim" aria-hidden="true" onClick={() => setDrawerOpen(false)} />}

      <main id="main" tabIndex={-1} className="main">
        {linked && route === "#/today" && <MyShiftsLink />}
        <Screen />
      </main>
    </div>
  );
}

function initials(s: string): string {
  const parts = s.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "A";
}
